import { Component, EventEmitter, Input, Output, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { MessageWithFile } from '../../models/chat.model';

export { MessageWithFile };

/** Allowed file types for upload */
const ALLOWED_CONTENT_TYPES = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain', 'text/markdown'];

/** Allowed file extensions */
const ALLOWED_EXTENSIONS = ['.pdf', '.docx', '.txt', '.md'];

/** Maximum file size in bytes (10MB) */
const MAX_FILE_SIZE = 10 * 1024 * 1024;

@Component({
	selector: 'app-chat-input',
	templateUrl: './chat-input.component.html',
	styleUrls: ['./chat-input.component.scss'],
})
export class ChatInputComponent implements AfterViewInit {
	@Input() hideAttach = false;
	@Input() disabled = false;
	@Output() messageSent = new EventEmitter<MessageWithFile>();
	@ViewChild('textareaRef') textareaRef!: ElementRef<HTMLTextAreaElement>;
	@ViewChild('fileInputRef') fileInputRef!: ElementRef<HTMLInputElement>;

	/** Tooltip text shown on the attach button, listing accepted file types and size limit */
	readonly attachTooltip = `Allowed file extensions: ${ALLOWED_EXTENSIONS.join(', ')}.\nMax. file size: ${MAX_FILE_SIZE / (1024 * 1024)} MB`;

	messageText = '';
	attachedFile: File | null = null;
	fileError: string | null = null;

	private readonly minHeight = 48;
	private readonly maxHeight = 200;

	ngAfterViewInit(): void {
		this.resizeTextarea();
	}

	onSend(): void {
		if (this.disabled) return;
		if (this.messageText.trim() || this.attachedFile) {
			this.messageSent.emit({
				message: this.messageText.trim(),
				file: this.attachedFile || undefined,
			});
			this.messageText = '';
			this.attachedFile = null;
			this.fileError = null;
			this.resetTextarea();
		}
	}

	onKeyDown(event: KeyboardEvent): void {
		if (event.key === 'Enter' && !event.shiftKey) {
			event.preventDefault();
			this.onSend();
		}
	}

	onInput(): void {
		this.resizeTextarea();
	}

	/** Trigger the hidden file input */
	onAttachClick = (): void => {
		this.fileInputRef?.nativeElement?.click();
	};

	/** Handle file selection from input */
	onFileSelected(event: Event): void {
		const input = event.target as HTMLInputElement;
		const file = input.files?.[0];

		// Reset input so same file can be selected again
		input.value = '';

		if (!file) {
			return;
		}

		// Validate file type
		const fileExtension = this.getFileExtension(file.name).toLowerCase();
		const isValidExtension = ALLOWED_EXTENSIONS.includes(fileExtension);
		const isValidMimeType = ALLOWED_CONTENT_TYPES.includes(file.type) || (fileExtension === '.md' && (file.type === '' || file.type === 'text/markdown'));

		if (!isValidExtension && !isValidMimeType) {
			this.fileError = `Unsupported file type. Allowed: PDF, DOCX, TXT, MD`;
			this.attachedFile = null;
			return;
		}

		// Validate file size
		if (file.size > MAX_FILE_SIZE) {
			this.fileError = `File exceeds 10MB limit`;
			this.attachedFile = null;
			return;
		}

		// File is valid
		this.attachedFile = file;
		this.fileError = null;
	}

	/** Remove the attached file */
	removeFile(): void {
		this.attachedFile = null;
		this.fileError = null;
	}

	/** Format file size for display */
	formatFileSize(bytes: number): string {
		if (bytes === 0) return '0 B';

		const units = ['B', 'KB', 'MB', 'GB'];
		const k = 1024;
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		const size = bytes / Math.pow(k, i);

		return `${size.toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
	}

	/** Check if send button should be enabled */
	canSend(): boolean {
		return !this.disabled && !!(this.messageText.trim() || this.attachedFile);
	}

	private getFileExtension(filename: string): string {
		const lastDot = filename.lastIndexOf('.');
		return lastDot >= 0 ? filename.substring(lastDot) : '';
	}

	private resizeTextarea(): void {
		const el = this.textareaRef?.nativeElement;
		if (!el) return;
		el.style.height = 'auto';
		const scrollHeight = el.scrollHeight;
		el.style.height = Math.min(Math.max(scrollHeight, this.minHeight), this.maxHeight) + 'px';
	}

	private resetTextarea(): void {
		const el = this.textareaRef?.nativeElement;
		if (!el) return;
		el.style.height = this.minHeight + 'px';
	}
}
